import * as React from 'react';
import { loadStripe } from '@stripe/stripe-js/pure';
import { ActionStatus } from 'thunkless';
import { useSelector } from 'react-redux';
import { orgStripeSessionSelector, orgStripeCheckoutStatusSelector } from '../../../redux/app/selectors/organization';
import { AppSecretsContext } from '../secrets/app-secrets';
import { Column } from './Column';
import { UILoader } from '../ui-elements/UILoader';
import { FeaturesMatrix } from './FeaturesMatrix';
import xMark from ':assets/images/static_pages/x_mark.svg';
import arrow from ':assets/images/static_pages/hand_drawn_arrow.svg';
import { v4PlanColumnDatas } from '../../../constants/pricing';
import { ConfirmationModal } from '../shared/ConfirmationModal';

interface CheckoutFlowProps {
  handleCloseModal: () => void;
}
export const CheckoutFlow = ({ handleCloseModal }: CheckoutFlowProps) => {
  const [seatCount, setSeatCount] = React.useState(1);
  const [selectedPlan, setSelectedPlan] = React.useState(null);
  const [togglePayPeriod, setTogglePayPeriod] = React.useState('annual');
  const [isShowContactSupport, setIsShowContactSupport] = React.useState(false);

  const stripeCheckoutStatus = useSelector(orgStripeCheckoutStatusSelector);
  const stripeSessionId = useSelector(orgStripeSessionSelector);
  const { stripePublishableKey } = React.useContext(AppSecretsContext);
  const { lite, standard, premium } = v4PlanColumnDatas;
  const columnDatas = [lite, standard, premium];

  const handleConfirmContactSupport = () => {
    setIsShowContactSupport(false);
    Object.keys(window.Avochato.widgets).forEach((key) => {
      window.Avochato.widgets[key].openWithOption('current customer');
    });
  };

  React.useEffect(() => {
    if (!stripeSessionId) return;
    if (!window.navigator.userAgent) return;

    // skip loading stripe on iphone/ipad
    if (window.navigator.userAgent.match(/Avochato for i(Pad|Phone)/)) return;

    const redirectToStripe = async () => {
      try {
        const response = await loadStripe(stripePublishableKey);
        response.redirectToCheckout({
          sessionId: stripeSessionId,
        });
      } catch (err) {
        console.log(err);
      }
    };

    redirectToStripe();
  }, [stripeSessionId, stripePublishableKey]);

  const isMonthlyPayPeriod = togglePayPeriod === 'monthly';

  const handleToggle = () => {
    setTogglePayPeriod(isMonthlyPayPeriod ? 'annual' : 'monthly');
  };

  const columns = columnDatas.map((columnData) => (
    <Column
      key={columnData.name}
      seatCount={seatCount}
      setSeatCount={setSeatCount}
      columnData={columnData}
      togglePayPeriod={togglePayPeriod}
      selectedPlan={selectedPlan}
      handleShowModal={() => setIsShowContactSupport(true)}
    />
  ));
  const toggleMonthlyClass = `option ${isMonthlyPayPeriod ? 'active' : ''}`;
  const toggleAnnualClass = `option ${isMonthlyPayPeriod ? '' : 'active'}`;

  return (
    <div id="checkout-flow">
      <ConfirmationModal
        showModal={isShowContactSupport}
        handleHideModal={() => setIsShowContactSupport(false)}
        handleEndSetup={handleConfirmContactSupport}
        header="You already have a Subscription."
        question="Please contact support."
        content=""
        cancelText="Nevermind"
        confirmText="Contact Support"
        buttonColor="bright-green"
      />
      <div className="checkout-header width-100 flex-col">
        {
          stripeCheckoutStatus === ActionStatus.BUSY
          && <div className="ui-loader"><UILoader /></div>
        }
        <div
          role="button"
          tabIndex={0}
          className="close-button"
          onKeyPress={handleCloseModal}
          onClick={handleCloseModal}
        >
          <img className="complete" src={xMark} alt="checkmark" />
        </div>
        <p className="fsz-36 pt-25 mb-5 center bold-900">Choose the plan that&apos;s right for you</p>
        <p className="align-self-c center fsz-16 gray" style={{ maxWidth: '510px' }}>
          Whether you are a solo-entrepreneur, growing start-up, or thriving enterprise,
          there is a plan for you.
        </p>
      </div>
      <button type="button" className="switch mt-35" onClick={handleToggle}>
        <p className={toggleMonthlyClass}>Pay Monthly</p>
        <p className={toggleAnnualClass}>Pay Annually</p>
      </button>
      <div style={{ marginBottom: '45px', transform: 'translate(120%, 10px)' }}>
        <img src={arrow} alt="arrow" />
        &nbsp;
        <span className="fsz-13 olive-green">Save 18%</span>
      </div>
      <section id="checkout-step-1">
        {columns}
      </section>
      <FeaturesMatrix />
    </div>
  );
};
